import React, { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  TooltipProps,
} from 'recharts'
import type { DashboardInvestmentTransactions } from '../../../lib/types/propsType_v1'
import { useBreakpoint } from '../../../lib/utils/hooks'
import {
  buildTransactionTypesByDate,
  calendarDateKey,
  formatAxisDate,
  formatAxisDollar,
} from '../dashboard-v2.utils'

type PerformanceChartProps = {
  title: string
  filters: { text: string; value: string }[]
  selected: string
  onChange: (value: string) => void
  series: { date: string; value: number }[]
  currentValueText: string
  lastUpdatedText: string
  /** Matched by calendar day; types use formatTransactionType (same as TransactionsTable). */
  transactions?: DashboardInvestmentTransactions[]
}

type ChartPoint = { date: string; value: number }

type PerformanceTooltipProps = {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: ChartPoint }>
  typesByDate: Map<string, string>
}

/** Props Recharts passes to Tooltip `content` (typed for eslint react/prop-types). */
type AreaChartTooltipRenderProps = Pick<TooltipProps<number, string>, 'active' | 'payload'>

const PerformanceTooltip: React.FC<PerformanceTooltipProps> = ({
  active,
  payload,
  typesByDate,
}) => {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload as ChartPoint | undefined
  if (!row?.date) return null
  const dateKey = calendarDateKey(row.date)
  const typeLine = dateKey ? typesByDate.get(dateKey) : undefined

  return (
    <div className='dashboard-v2__chart-tooltip'>
      <div className='dashboard-v2__chart-tooltip__label'>{formatAxisDate(row.date)}</div>
      <div className='dashboard-v2__chart-tooltip__value'>
        {formatAxisDollar(Number(row.value))}
      </div>
      {typeLine ? (
        <div className='dashboard-v2__chart-tooltip__meta'>{typeLine}</div>
      ) : typesByDate.size > 0 ? (
        <div className='dashboard-v2__chart-tooltip__meta dashboard-v2__chart-tooltip__meta--muted'>
          No transaction recorded this day
        </div>
      ) : null}
    </div>
  )
}

export const PerformanceChart: React.FC<PerformanceChartProps> = ({
  title,
  filters,
  selected,
  onChange,
  series,
  currentValueText,
  lastUpdatedText,
  transactions,
}) => {
  const typesByDate = useMemo(() => buildTransactionTypesByDate(transactions), [transactions])
  const { breakpointRange } = useBreakpoint()
  const isCompact = breakpointRange === 'xs' || breakpointRange === 'sm'
  const chartHeight = isCompact ? 220 : 280
  const yAxisWidth = isCompact ? 52 : 72

  return (
    <section className='dashboard-v2__chart-card'>
      <div className='card-head'>
        <h2>{title}</h2>
        <div className='filters'>
          {filters.map((item) => (
            <button
              key={item.value}
              type='button'
              className={selected === item.value ? 'active' : ''}
              onClick={() => onChange(item.value)}
            >
              {item.text}
            </button>
          ))}
        </div>
      </div>
      <h3 className='highlight value-fit'>{currentValueText}</h3>
      <p className='growth'>{lastUpdatedText}</p>
      <div className='chart-wrap'>
        <ResponsiveContainer width='100%' height={chartHeight}>
          <AreaChart
            data={series}
            margin={{ left: isCompact ? 0 : 8, right: 8, top: 8, bottom: 0 }}
          >
            <defs>
              <linearGradient id='investedFill' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor='#0D86FF' stopOpacity={0.45} />
                <stop offset='95%' stopColor='#0D86FF' stopOpacity={0.08} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis
              dataKey='date'
              tickFormatter={formatAxisDate}
              tick={{ fontSize: 12, fill: '#63768b' }}
              minTickGap={24}
            />
            <YAxis
              width={yAxisWidth}
              tickFormatter={formatAxisDollar}
              tick={{ fontSize: 12, fill: '#63768b' }}
              tickMargin={8}
              allowDecimals
              domain={['auto', 'auto']}
            />
            <Tooltip
              wrapperClassName='dashboard-v2__chart-tooltip-wrap'
              content={(tooltipProps: AreaChartTooltipRenderProps) => (
                <PerformanceTooltip
                  active={tooltipProps.active}
                  payload={tooltipProps.payload}
                  typesByDate={typesByDate}
                />
              )}
            />
            <Area
              type='monotone'
              dataKey='value'
              stroke='#0D86FF'
              fill='url(#investedFill)'
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  )
}
